import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { setTimezone } from "../lib/dates";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import B2BDeptSettings from "./settings/B2BDeptSettings";
import DataSafety from "./settings/DataSafety";

const DEPTS = [["b2b", "B2B Admissions"], ["b2c", "B2C Admissions"], ["immigration", "Immigration / Filing"], ["leads", "Lead Management"], ["finance", "Finance"]];
const TZS = ["America/Toronto", "America/Vancouver", "America/Edmonton", "America/Winnipeg", "America/Halifax", "Asia/Kolkata", "UTC"];

export default function Settings() {
  const { user } = useAuth();
  const [dept, setDept] = useState("b2b");
  const isAdmin = user?.role === "admin";
  return (
    <div className="space-y-6">
      <div><h1 className="heading-font text-3xl font-bold text-slate-900">Settings</h1><p className="text-sm text-slate-500 mt-1">Global settings, then each department's lists, workflow, reminders and access.</p></div>
      <div className="bg-white p-5 rounded-xl border border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div><div className="text-xs uppercase text-slate-500">Name</div><div className="font-medium">{user?.name}</div></div>
        <div><div className="text-xs uppercase text-slate-500">Email</div><div className="font-medium truncate">{user?.email}</div></div>
        <div><div className="text-xs uppercase text-slate-500">Role</div><div className="capitalize">{user?.role?.replace(/_/g, " ")}</div></div>
        <div><div className="text-xs uppercase text-slate-500">Currency</div><div>CAD (Canadian Dollar)</div></div>
      </div>
      {isAdmin && <GlobalSettings />}
      <div className="bg-white p-4 rounded-xl border border-slate-200 flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Department</span>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger data-testid="settings-dept-select" className="w-[240px] h-9"><SelectValue /></SelectTrigger>
          <SelectContent>{DEPTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {dept === "b2b" ? <B2BDeptSettings recipients={isAdmin ? <WorkReportRecipients /> : null} />
        : <div data-testid="settings-dept-phase2" className="bg-white p-6 rounded-xl border border-dashed border-slate-300 text-sm text-slate-600">
            {DEPTS.find(d => d[0] === dept)[1]} settings arrive in Phase 2. Until then this department keeps its current access rules.
          </div>}
      {isAdmin && <DataSafety />}
    </div>
  );
}

function GlobalSettings() {
  const [tz, setTz] = useState("");
  useEffect(() => { api.get("/settings/global").then(r => setTz(r.data.timezone)); }, []);
  const save = async (v) => {
    try { const r = await api.put("/settings/global", { timezone: v }); setTz(r.data.timezone); setTimezone(r.data.timezone); toast.success("Timezone saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  return (
    <div data-testid="settings-global" className="bg-white p-5 rounded-xl border border-slate-200 space-y-2">
      <div className="heading-font font-semibold text-slate-900">Global Settings</div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-slate-600">Timezone used for "today" and deadline countdowns</span>
        <Select value={tz} onValueChange={save}>
          <SelectTrigger data-testid="settings-timezone" className="w-[220px] h-9"><SelectValue placeholder="Loading…" /></SelectTrigger>
          <SelectContent>{TZS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
}

function WorkReportRecipients() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    api.get("/work-report/recipients")
      .then(r => { if (live) { setForm(f => f || { manager_email: r.data.manager_email, director_email: r.data.director_email }); } })
      .catch(() => { if (live) { setForm(f => f || { manager_email: "", director_email: "" }); } });
    return () => { live = false; };
  }, []);
  if (!form) return null;
  const save = async () => {
    setSaving(true);
    try { await api.put("/work-report/recipients", form); toast.success("Work report recipients saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="settings-work-report-recipients" className="space-y-3">
      <div className="text-sm font-medium text-slate-800">Work report recipients</div>
      <p className="text-xs text-slate-500">Reports go To the Manager with the Director in CC. When the Manager sends their own report, it goes straight to the Director.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1"><div className="text-xs text-slate-600">Manager email</div>
          <Input data-testid="settings-manager-email" value={form.manager_email} onChange={e => setForm({ ...form, manager_email: e.target.value })} /></div>
        <div className="space-y-1"><div className="text-xs text-slate-600">Director email (CC)</div>
          <Input data-testid="settings-director-email" value={form.director_email} onChange={e => setForm({ ...form, director_email: e.target.value })} /></div>
      </div>
      <Button data-testid="settings-recipients-save" size="sm" onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">{saving ? "Saving…" : "Save recipients"}</Button>
    </div>
  );
}
